import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  OnDestroy,
  viewChild,
  viewChildren,
  signal,
  computed,
  effect,
  QueryList,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { getTranslationDistance, getDistance, map } from './grid-utils';

export interface GridItem {
  id: string | number;
  imageUrl: string;
  alt?: string;
}

export interface GridOptions {
  duration: number;
  ease: string;
  scale: number;
  skew: number;
  maxRotation: number;
  spread: number;
  maxDistance: number;
}

@Component({
  selector: 'app-grid-make-way-effect',
  imports: [],
  templateUrl: './grid-make-way-effect.component.html',
  styleUrl: './grid-make-way-effect.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridMakeWayEffectComponent implements OnDestroy {
  // Platform detection
  private platformId = inject(PLATFORM_ID);

  // Inputs - now only options needed since content is projected
  options = input<Partial<GridOptions>>({});
  cssClasses = input<string>('row gutter-y-2 gutter-x-2');

  // CSS selector for grid items (customizable)
  itemSelector = input<string>('.grid__item');

  // Default options
  private readonly defaultOptions: GridOptions = {
    duration: 1,
    ease: 'elastic.out(0.5)',
    scale: 3,
    skew: 0,
    maxRotation: 20,
    spread: 150,
    maxDistance: 700,
  };

  // Template refs - Pure Angular approach
  grid = viewChild<ElementRef<HTMLElement>>('grid');

  // Reactive state
  public expandedIndex = signal<number>(-1);
  previousExpandedIndex = signal<number>(-1);
  isInitialized = signal(false);

  // Computed values
  mergedOptions = computed(() => ({ ...this.defaultOptions, ...this.options() }));

  // Animation timeline
  private tl?: gsap.core.Timeline;
  private clickHandlers = new Map<HTMLElement, () => void>();
  private gridItemElements: HTMLElement[] = [];

  constructor() {
    // Initialize after render - only in browser
    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.initializeGrid();
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private initializeGrid(): void {
    const gridElement = this.grid()?.nativeElement;
    if (!gridElement) {
      console.warn('Grid element not found');
      return;
    }

    try {
      // Find all grid items using the selector
      this.findGridItems();
      this.setupEventListeners();
      this.isInitialized.set(true);
    } catch (error) {
      console.error('Failed to initialize grid:', error);
    }
  }

  private findGridItems(): void {
    const gridElement = this.grid()?.nativeElement;
    if (!gridElement) return;

    // Query for grid items within the grid container
    this.gridItemElements = Array.from(gridElement.querySelectorAll<HTMLElement>(this.itemSelector()));
  }

  private setupEventListeners(): void {
    // Clean up existing listeners
    this.cleanupEventListeners();

    // Set up new listeners for each grid item
    this.gridItemElements.forEach((element, index) => {
      const handler = () => this.expandItem(index);

      element.addEventListener('click', handler);
      this.clickHandlers.set(element, handler);

      // Add accessibility attributes
      element.setAttribute('tabindex', '0');
      element.setAttribute('role', 'button');
      element.setAttribute('aria-label', `Grid item ${index + 1}`);
      element.setAttribute('aria-expanded', 'false');

      // Add keyboard support
      const keyHandler = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.expandItem(index);
        }
      };
      element.addEventListener('keydown', keyHandler);
    });
  }

  private cleanupEventListeners(): void {
    this.clickHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.clickHandlers.clear();
  }

  private expandItem(clickedIndex: number): void {
    // Kill any existing timeline
    this.tl?.kill();

    // Update expanded state
    const currentExpanded = this.expandedIndex();
    this.previousExpandedIndex.set(currentExpanded !== -1 && currentExpanded !== clickedIndex ? currentExpanded : -1);
    this.expandedIndex.set(currentExpanded === clickedIndex ? -1 : clickedIndex);

    // Update ARIA attributes
    this.updateAriaAttributes(clickedIndex);

    // Get the clicked element
    const clickedElement = this.gridItemElements[clickedIndex];

    if (!clickedElement) return;

    // Create animation timeline
    this.createAnimationTimeline(clickedElement, this.gridItemElements, clickedIndex);
  }

  private updateAriaAttributes(clickedIndex: number): void {
    this.gridItemElements.forEach((element, index) => {
      const isExpanded = this.expandedIndex() === index;
      element.setAttribute('aria-expanded', isExpanded.toString());
      element.classList.toggle('grid__item--expanded', isExpanded);
    });
  }

  private createAnimationTimeline(clickedElement: HTMLElement, allElements: HTMLElement[], clickedIndex: number): void {
    const options = this.mergedOptions();
    const isExpanding = this.expandedIndex() !== -1;

    this.tl = gsap.timeline({
      defaults: {
        duration: options.duration,
        ease: options.ease,
      },
    });

    // Animate clicked item
    this.animateClickedItem(clickedElement, options, isExpanding);

    // Handle previously expanded item
    const prevIndex = this.previousExpandedIndex();
    if (prevIndex !== -1 && prevIndex < allElements.length) {
      this.animatePreviousItem(allElements[prevIndex]);
    }

    // Animate other items
    this.animateOtherItems(clickedElement, allElements, clickedIndex, options, isExpanding);
  }

  private animateClickedItem(element: HTMLElement, options: GridOptions, isExpanding: boolean): void {
    // Set z-index
    this.tl!.set(
      element,
      {
        zIndex: isExpanding ? 999 : 1,
      },
      isExpanding ? 'start' : 'end'
    );

    // Main animation
    if (options.skew) {
      this.tl!.to(
        element,
        {
          duration: options.duration * 0.4,
          ease: 'sine.in',
          scale: 1 + (options.scale - 1) / 2,
          skewX: isExpanding ? options.skew : -options.skew,
          skewY: isExpanding ? options.skew : -options.skew,
          x: 0,
          y: 0,
          rotation: 0,
        },
        'start'
      ).to(
        element,
        {
          duration: options.duration * 0.6,
          ease: 'power4',
          scale: isExpanding ? options.scale : 1,
          skewX: 0,
          skewY: 0,
        },
        `start+=${options.duration * 0.4}`
      );
    } else {
      this.tl!.to(
        element,
        {
          scale: isExpanding ? options.scale : 1,
          x: 0,
          y: 0,
          rotation: 0,
        },
        'start'
      );
    }
  }

  private animatePreviousItem(prevElement: HTMLElement): void {
    this.tl!.set(prevElement, { zIndex: 1 }, 'start').to(
      prevElement,
      {
        scale: 1,
        x: 0,
        y: 0,
        rotation: 0,
      },
      'start'
    );
  }

  private animateOtherItems(
    clickedElement: HTMLElement,
    allElements: HTMLElement[],
    clickedIndex: number,
    options: GridOptions,
    isExpanding: boolean
  ): void {
    allElements.forEach((otherElement, index) => {
      if (index === clickedIndex) return;

      const { x, y } = isExpanding
        ? getTranslationDistance(otherElement, clickedElement, options.spread, options.maxDistance)
        : { x: 0, y: 0 };

      const distance = getDistance(otherElement, clickedElement);
      const zIndex = Math.round(map(distance, 0, 100000, 998, 1));

      const rotationInterval = options.maxRotation ? Math.max(Math.round(map(distance, 0, 500, options.maxRotation, 0)), 0) : 0;

      this.tl!.set(
        otherElement,
        {
          zIndex: isExpanding ? zIndex : 1,
        },
        isExpanding ? 'start' : 'end'
      ).to(
        otherElement,
        {
          x,
          y,
          rotation: isExpanding ? gsap.utils.random(-rotationInterval, rotationInterval) : 0,
        },
        'start'
      );
    });
  }

  private cleanup(): void {
    this.tl?.kill();
    this.cleanupEventListeners();
  }

  /**
   * Public method to programmatically trigger grid reinitialization
   */
  reinitialize(): void {
    this.cleanup();
    this.isInitialized.set(false);
    this.expandedIndex.set(-1);
    this.previousExpandedIndex.set(-1);

    setTimeout(() => this.initializeGrid(), 0);
  }

  /**
   * Public method to expand a specific item by index
   */
  expandItemByIndex(index: number): void {
    if (index >= 0 && index < this.gridItemElements.length) {
      this.expandItem(index);
    }
  }

  /**
   * Public method to collapse all items
   */
  collapseAll(): void {
    if (this.expandedIndex() !== -1) {
      this.expandItem(this.expandedIndex());
    }
  }
}
